import React, { useState } from "react";
import { Customer, syncCustomer, CustomerSyncModel, reactivateCustomer } from "@/api/customers";
import { Link } from "react-router-dom";
import { DataGrid, DataGridColumn } from "@/ui-controls/DataGrid";
import MoreActionsButton from "@/ui-controls/MoreActionsButton";
import DeleteCustomer from "@/components/customers/DeleteCustomer";
import { Configuration } from "@/config";
import UpdateCustomer from "@/components/customers/UpdateCustomer";
import SyncCustomerDialog from "@/components/customers/SyncCustomerDialog";
import { IconButton, TooltipHost } from "@fluentui/react";

interface Props {
  CustomerList: Customer[];
  onDelete: (customer: Customer) => any;
  onUpdate?: (customer: Customer) => any;
  onShowSuccess?: (message: string) => void;
  onShowError?: (message: string) => void;
}

enum MenuActionType {
  DEACTIVATE_CUSTOMER = 1,
  UPDATE_CUSTOMER = 2,
  ADD_USERS = 3,
  ADD_OAPI_INTEGRATIONS = 4,
  REACTIVATE_CUSTOMER = 5,
}

const {
  config: { PCS_SOFTWARE_CUSTOMER_ID },
} = new Configuration();

const CustomerDetailList = (props: Props) => {
  const countBadgeStyle: React.CSSProperties = {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    minWidth: 32,
    height: 28,
    padding: "0 10px",
    borderRadius: 14,
    backgroundColor: "#f3f2f1",
    color: "#323130",
    fontWeight: 600,
    textDecoration: "none",
  };

  const [selectedCustomer, setSelectedCustomer] = useState<
    Customer | undefined
  >(undefined);
  const [menuActionType, setMenuActionType] = useState<
    MenuActionType | undefined
  >(undefined);
  const [selectedSyncCustomer, setSelectedSyncCustomer] =
    useState<Customer | null>(null);
  const [isSyncDialogOpen, setIsSyncDialogOpen] = useState(false);

  const columns: DataGridColumn[] = [
    {
      key: "id",
      name: "Id",
      fieldName: "Id",
      minWidth: 50,
      maxWidth: 60,
      isRowHeader: true,
      isResizable: true,
      isPadded: true,
    },
    {
      key: "name",
      name: "Name",
      fieldName: "Name",
      minWidth: 150,
      maxWidth: 250,
      isRowHeader: true,
      isResizable: true,
      isPadded: true,
    },
    {
      key: "type",
      name: "Customer Type",
      fieldName: "CustomerType",
      minWidth: 150,
      maxWidth: 250,
      isRowHeader: true,
      isResizable: true,
      isPadded: true,
    },
    {
      key: "customerNumber",
      name: "Customer Number",
      fieldName: "CustomerNumber",
      minWidth: 150,
      maxWidth: 250,
      isRowHeader: true,
      isResizable: true,
      isPadded: true,
    },
    {
      key: "databaseIdentifier",
      name: "Database Identifier",
      fieldName: "DatabaseIdentifier",
      minWidth: 100,
      maxWidth: 150,
      isRowHeader: true,
      isResizable: true,
      isPadded: true,
    },
    {
      key: "environment",
      name: "Environment",
      fieldName: "Environment",
      minWidth: 100,
      maxWidth: 200,
      isRowHeader: true,
      isResizable: true,
      isPadded: true,
    },
    {
      key: "entityId",
      name: "Entity ID",
      fieldName: "EntityId",
      minWidth: 100,
      maxWidth: 150,
      isRowHeader: true,
      isResizable: true,
      isPadded: true,
      onRender: (item: Customer) => {
        const canSync =
          item.IsActive && item.Id !== 1 && item.CustomerType === "Carrier";

        return (
          <div
            style={{
              display: "flex",
              justifyContent: "flex-start",
              alignItems: "center",
              height: "100%",
            }}
          >
            {item.EntityId ? (
              <div
                style={{
                  backgroundColor: "#d4edda",
                  color: "#155724",
                  border: "1px solid #c3e6cb",
                  borderRadius: "12px",
                  padding: "4px 8px",
                  fontSize: "12px",
                  fontWeight: "600",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: canSync ? "space-between" : "center",
                  minWidth: "40px",
                  gap: "4px",
                }}
              >
                <span>{item.EntityId}</span>
                {canSync && (
                  <TooltipHost content="Update sync with FrostByte">
                    <IconButton
                      iconProps={{ iconName: "Sync" }}
                      title="Update sync with FrostByte"
                      ariaLabel="Update customer sync with FrostByte"
                      onClick={() => {
                        setSelectedSyncCustomer(item);
                        setIsSyncDialogOpen(true);
                      }}
                      styles={{
                        root: {
                          width: 16,
                          height: 16,
                          minWidth: 16,
                          color: "#155724",
                          padding: 0,
                        },
                        rootHovered: {
                          backgroundColor: "rgba(21, 87, 36, 0.1)",
                        },
                        icon: {
                          fontSize: 10,
                        },
                      }}
                    />
                  </TooltipHost>
                )}
              </div>
            ) : (
              canSync && (
                <div
                  style={{
                    backgroundColor: "#f8d7da",
                    color: "#721c24",
                    border: "1px solid #f5c6cb",
                    borderRadius: "12px",
                    padding: "4px 8px",
                    fontSize: "12px",
                    fontWeight: "600",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "flex-end",
                    minWidth: "40px",
                  }}
                >
                  <TooltipHost content="Sync with FrostByte">
                    <IconButton
                      iconProps={{ iconName: "Sync" }}
                      title="Sync with FrostByte"
                      ariaLabel="Sync customer with FrostByte"
                      onClick={() => {
                        setSelectedSyncCustomer(item);
                        setIsSyncDialogOpen(true);
                      }}
                      styles={{
                        root: {
                          width: 16,
                          height: 16,
                          minWidth: 16,
                          color: "#721c24",
                          padding: 0,
                        },
                        rootHovered: {
                          backgroundColor: "rgba(114, 28, 36, 0.1)",
                        },
                        icon: {
                          fontSize: 10,
                        },
                      }}
                    />
                  </TooltipHost>
                </div>
              )
            )}
          </div>
        );
      },
    },
    {
      key: "NoUsers",
      name: "Users",
      fieldName: "UsersCount",
      minWidth: 70,
      maxWidth: 100,
      isRowHeader: true,
      isResizable: true,
      isPadded: true,
      onRender: (item: Customer) =>
        item.IsActive ? (
          <Link to={`/customers/${item.Id}/users`} style={countBadgeStyle}>
            {item.UsersCount}
          </Link>
        ) : (
          <span style={{ color: "#a19f9d" }}>{item.UsersCount}</span>
        ),
    },
    {
      key: "OApiIntegrations",
      name: "OAPI Integrations",
      fieldName: "AppRegistrationsCount",
      minWidth: 70,
      maxWidth: 100,
      isRowHeader: true,
      isResizable: true,
      isPadded: true,
      onRender: (item: Customer) =>
        item.IsActive ? (
          <Link
            to={`/customers/${item.Id}/open-api-integrations`}
            style={countBadgeStyle}
          >
            {item.AppRegistrationsCount}
          </Link>
        ) : (
          <span style={{ color: "#a19f9d" }}>{item.AppRegistrationsCount}</span>
        ),
    },
    {
      key: "Actions",
      name: "",
      minWidth: 50,
      maxWidth: 70,
      onRender: (item: Customer) => {
        const isInactive = !item.IsActive;

        const menuItems = isInactive
          ? [
              {
                key: "reactivate",
                text: "Reactivate customer",
                iconProps: { iconName: "Redo" },
                onClick: async () => {
                  try {
                    await reactivateCustomer(item.Id);

                    if (props.onUpdate) {
                      props.onUpdate(item);
                    }

                    if (props.onShowSuccess) {
                      props.onShowSuccess(
                        `Customer "${item.Name}" has been reactivated.`
                      );
                    }
                  } catch (error: any) {
                    if (props.onShowError) {
                      const message =
                        error?.response?.data?.message ||
                        error?.message ||
                        "Failed to reactivate customer";
                      props.onShowError(message);
                    }
                  }
                },
              },
            ]
          : [
              {
                key: "deactivate",
                text: "Deactivate customer",
                iconProps: { iconName: "Delete" },
                disabled: item.Id === PCS_SOFTWARE_CUSTOMER_ID,
                onClick: () => {
                  setMenuActionType(MenuActionType.DEACTIVATE_CUSTOMER);
                  setSelectedCustomer(item);
                },
              },
              {
                key: "update",
                text: "Update customer",
                iconProps: { iconName: "Edit" },
                onClick: () => {
                  setMenuActionType(MenuActionType.UPDATE_CUSTOMER);
                  setSelectedCustomer(item);
                },
              },
              {
                key: "addSection",
                text: "Add",
                iconProps: { iconName: "Add" },
                subMenuProps: {
                  items: [
                    {
                      key: "addUsers",
                      text: "Users",
                      title: "Add users",
                      onClick: () => {
                        setMenuActionType(MenuActionType.ADD_USERS);
                        setSelectedCustomer(item);
                      },
                    },
                    {
                      key: "addOApiIntegrations",
                      text: "OAPI Integrations",
                      title: "Add OAPI Integrations",
                      onClick: () => {
                        setMenuActionType(MenuActionType.ADD_OAPI_INTEGRATIONS);
                        setSelectedCustomer(item);
                      },
                    },
                  ],
                },
              },
            ];

        return <MoreActionsButton menu={menuItems} />;
      },
    },
  ];

  const cleanCustomerAction = () => {
    setSelectedCustomer(undefined);
    setMenuActionType(undefined);
  };

  const handleSyncCustomer = async (
    customer: Customer,
    businessLineId: number,
    databaseName: string,
    entityName: string
  ) => {
    try {
      const syncModel: CustomerSyncModel = {
        EntityId: customer.EntityId,
        ExternalId: customer.Id,
        DatabaseName: databaseName,
        EntityName: entityName,
        BusinessLineId: businessLineId,
        EntityNumber: customer.CustomerNumber || 0,
        EntityDatabaseLetter: customer.DatabaseIdentifier || "",
      };

      const result = await syncCustomer(syncModel);

      if (props.onUpdate) {
        props.onUpdate(customer);
      }

      const businessLineName =
        businessLineId === 1 ? "Truckload Dispatch" : "Truckload Brokerage";
      const actionText = customer.EntityId ? "updated in" : "synced with";

      if (props.onShowSuccess) {
        props.onShowSuccess(
          `Customer "${databaseName}" has been ${actionText} FrostByte successfully! Business Line: ${businessLineName}`
        );
      }
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to sync customer with FrostByte";

      if (props.onShowError) {
        props.onShowError(errorMessage);
      }

      throw new Error(errorMessage);
    }
  };

  const closeSyncDialog = () => {
    setIsSyncDialogOpen(false);
    setSelectedSyncCustomer(null);
  };

  const renderCustomerAction = () => {
    if (!selectedCustomer || !menuActionType) return null;

    switch (menuActionType) {
      case MenuActionType.DEACTIVATE_CUSTOMER:
        return (
          <DeleteCustomer
            customer={selectedCustomer}
            onClose={cleanCustomerAction}
            onDelete={(customer) => {
              props.onDelete(customer);
            }}
          />
        );
      case MenuActionType.UPDATE_CUSTOMER:
        return (
          <UpdateCustomer
            customer={selectedCustomer}
            onClose={cleanCustomerAction}
            onUpdate={(customer) => {
              if (props.onUpdate) {
                props.onUpdate(customer);
              }
            }}
          />
        );
      case MenuActionType.ADD_USERS:
      // return props.Application?.Id !== undefined ? (
      //   <AppCustomerUsers
      //     ApplicationId={props.Application.Id}
      //     CustomerId={selectedCustomer.Id}
      //     ShowOnlyNewAppCustomerUserPanel={true}
      //     onCloseNewAppCustomerUserPanel={cleanCustomerAction}
      //     onSaveAppCustomerUsers={(users) =>
      //       (selectedCustomer.UsersCount = users.length)
      //     }
      //   />
      // ) : null;
      default:
        return null;
    }
  };

  return (
    <>
      <DataGrid items={props.CustomerList} columns={columns} />
      {renderCustomerAction()}
      <SyncCustomerDialog
        customer={selectedSyncCustomer}
        isOpen={isSyncDialogOpen}
        onDismiss={closeSyncDialog}
        onSync={handleSyncCustomer}
      />
    </>
  );
};

export default CustomerDetailList;
